import React, { useEffect, useState } from 'react';
import { 
  Settings, User, CreditCard, Shield, Bell, Globe, Save, Loader2, LogOut, 
  ExternalLink, Zap, Lock, Key, Copy, Plus, Trash2, Check, RefreshCw, 
  AlertTriangle, Cpu, CheckCircle, Smartphone, CheckCircle2, Send, HelpCircle 
} from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { updateProfile, updatePassword } from 'firebase/auth';
import { useToast } from './Toast';
import { useLanguage } from '../src/lib/LanguageContext';

interface SettingsViewProps {
  initialTab?: 'profile' | 'connector' | 'subscription' | 'security' | 'notifications' | 'integrations';
}

export const SettingsView: React.FC<SettingsViewProps> = ({ initialTab = 'profile' }) => {
  const { showToast } = useToast();
  const { language, setLanguage, t } = useLanguage();
  
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'connector' | 'subscription' | 'security' | 'notifications' | 'integrations'>(initialTab);

  // Form States
  const [displayName, setDisplayName] = useState('');
  const [prefLanguage, setPrefLanguage] = useState<'pt' | 'en' | 'es'>('pt');
  
  // Conector States
  const [connectorSettings, setConnectorSettings] = useState({
    neuralReplacement: true,
    smartCapture: true,
    behaviorTracking: true
  });

  // Security States
  const [passwords, setPasswords] = useState({
    current: '',
    newPass: '',
    confirm: ''
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [isMfaVerifying, setIsMfaVerifying] = useState(false);
  const [mfaConfigured, setMfaConfigured] = useState(false);
  const [activeSessions, setActiveSessions] = useState([
    { id: 'sess-1', device: 'Chrome / Windows', ip: '177.34.128.45', location: 'São Paulo, BR', active: true, label: 'Sessão Atual' },
    { id: 'sess-2', device: 'Safari / iPhone 15 Pro', ip: '186.204.11.90', location: 'Rio de Janeiro, BR', active: false, label: '3 horas atrás' },
    { id: 'sess-3', device: 'Firefox / Mac OS', ip: '201.55.98.112', location: 'Curitiba, BR', active: false, label: '2 dias atrás' }
  ]);
  const [revokingSessions, setRevokingSessions] = useState(false);

  // Notification States
  const [notificationSettings, setNotificationSettings] = useState({
    emailConversions: true,
    weeklyReport: true,
    securityAlerts: true,
    marketingUpdates: false
  });

  // Integration States
  const [integrationSettings, setIntegrationSettings] = useState({
    gaId: 'G-74892305-1',
    pixelId: '89104829302193',
    pixelToken: 'EAAFZB19z3O4BA...',
    webhookUrl: '',
    webhookEvents: {
      leadCaptured: true,
      planUpdated: false,
      domainConfigured: true
    }
  });
  
  // Custom Card State
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    brand: 'MasterCard',
    last4: '4242',
    expiry: '12/28',
    holderName: 'Joseval Pereira'
  });
  const [cardForm, setCardForm] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: ''
  });

  // Webhook Test States
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState<any>(null);

  // Subtitles translations list
  const getTabLabel = (tab: string) => {
    switch (tab) {
      case 'profile': return language === 'en' ? 'Profile' : (language === 'es' ? 'Perfil' : 'Perfil');
      case 'connector': return language === 'en' ? 'Quantum Connector' : (language === 'es' ? 'Conector Quantum' : 'Conector Quantum');
      case 'subscription': return language === 'en' ? 'Subscription' : (language === 'es' ? 'Suscripción' : 'Assinatura');
      case 'security': return language === 'en' ? 'Security' : (language === 'es' ? 'Seguridad' : 'Segurança');
      case 'notifications': return language === 'en' ? 'Notifications' : (language === 'es' ? 'Notificaciones' : 'Notificações');
      case 'integrations': return language === 'en' ? 'Integrations' : (language === 'es' ? 'Integraciones' : 'Integrações');
      default: return tab;
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;

    const unsubscribe = onSnapshot(doc(db, 'users', auth.currentUser.uid), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setUserData(data);
        
        // Load stored settings safely
        if (data.displayName && !displayName) {
          setDisplayName(data.displayName);
        }
        if (data.language) {
          setPrefLanguage(data.language);
        }
        if (data.connectorSettings) {
          setConnectorSettings(prev => ({ ...prev, ...data.connectorSettings }));
        }
        if (data.notificationSettings) {
          setNotificationSettings(prev => ({ ...prev, ...data.notificationSettings }));
        }
        if (data.securitySettings) {
          setTwoFactorEnabled(data.securitySettings.twoFactorEnabled || false);
          setMfaConfigured(data.securitySettings.twoFactorEnabled || false);
        }
        if (data.integrationSettings) {
          setIntegrationSettings(prev => ({ ...prev, ...data.integrationSettings }));
        }
        if (data.cardDetails) {
          setCardDetails(prev => ({ ...prev, ...data.cardDetails }));
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    setSaving(true);
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      
      if (activeTab === 'profile') {
        const payload: any = { displayName };
        if (prefLanguage !== userData?.language) {
          payload.language = prefLanguage;
        }
        
        await updateDoc(userRef, payload);
        
        await updateProfile(auth.currentUser, {
          displayName
        });

        if (prefLanguage !== language) {
          setLanguage(prefLanguage);
        }
        
        showToast(
          language === 'en' ? 'Profile updated successfully!' : 
          (language === 'es' ? '¡Perfil actualizado con éxito!' : 'Perfil atualizado com sucesso!'), 
          'success'
        );
      } 
      else if (activeTab === 'connector') {
        await updateDoc(userRef, {
          connectorSettings
        });
        showToast(
          language === 'en' ? 'Quantum Connector settings saved!' : 
          (language === 'es' ? 'Configuración del Conector guardada.' : 'Configurações do Conector salvas com sucesso!'), 
          'success'
        );
      }
      else if (activeTab === 'security') {
        // Handle password change if filled
        if (passwords.newPass) {
          if (passwords.newPass !== passwords.confirm) {
            showToast(
              language === 'en' ? 'Passwords do not match.' : 
              (language === 'es' ? 'Las contraseñas no coinciden.' : 'A confirmação de senha não coincide.'), 
              'error'
            );
            setSaving(false);
            return;
          }
          if (passwords.newPass.length < 6) {
            showToast(
              language === 'en' ? 'Password must be at least 6 characters.' : 
              (language === 'es' ? 'La contraseña debe tener al menos 6 caracteres.' : 'A nova senha deve ter no mínimo 6 caracteres.'), 
              'error'
            );
            setSaving(false);
            return;
          }
          
          try {
            await updatePassword(auth.currentUser, passwords.newPass);
            showToast(
              language === 'en' ? 'Password updated successfully!' : 
              (language === 'es' ? '¡Contraseña actualizada con éxito!' : 'Senha atualizada com sucesso!'), 
              'success'
            );
          } catch (pwdErr: any) {
            console.error(pwdErr);
            if (pwdErr.code === 'auth/requires-recent-login') {
              showToast(
                language === 'en' ? 'Please log out and sign in again to change password.' : 
                (language === 'es' ? 'Por favor, reinicie sesión para cambiar la contraseña.' : 'Por motivos de segurança, saia da conta e entre novamente para alterar sua senha.'), 
                'error'
              );
            } else {
              showToast(
                language === 'en' ? 'Auth error. Direct passwords cannot be changed for social Google sign-in accounts.' : 
                (language === 'es' ? 'La seguridad de Google bloquea este cambio directo.' : 'Suas credenciais são gerenciadas diretamente pelo login seguro do Google.'), 
                'info'
              );
            }
          }
        }
        
        await updateDoc(userRef, {
          securitySettings: {
            twoFactorEnabled: mfaConfigured
          }
        });
        
        showToast(
          language === 'en' ? 'Security settings saved.' : 
          (language === 'es' ? 'Ajustes de seguridad guardados.' : 'Configurações de segurança salvas.'), 
          'success'
        );
        
        setPasswords({ current: '', newPass: '', confirm: '' });
      }
      else if (activeTab === 'notifications') {
        await updateDoc(userRef, {
          notificationSettings
        });
        showToast(
          language === 'en' ? 'Notification channels configured.' : 
          (language === 'es' ? 'Ajustes de notificaciones guardados.' : 'Preferências de notificação salvas!'), 
          'success'
        );
      }
      else if (activeTab === 'integrations') {
        await updateDoc(userRef, {
          integrationSettings
        });
        showToast(
          language === 'en' ? 'Integrations saved successfully!' : 
          (language === 'es' ? '¡Integraciones guardadas correctamente!' : 'Integrações salvas com sucesso!'), 
          'success'
        );
      }
    } catch (error) {
      console.error(error);
      showToast('Erro ao salvar alterações. Tente novamente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Helper session revoker
  const handleRevokeSessions = () => {
    setRevokingSessions(true);
    setTimeout(() => {
      setActiveSessions([
        { id: 'sess-1', device: 'Chrome / Windows', ip: '177.34.128.45', location: 'São Paulo, BR', active: true, label: 'Sessão Atual' }
      ]);
      setRevokingSessions(false);
      showToast(
        language === 'en' ? 'Other active sessions revoked successfully.' : 
        (language === 'es' ? 'Otras sesiones revocadas' : 'Todas as demais sessões foram encerradas com segurança!'), 
        'success'
      );
    }, 1200);
  };

  // Webhook Test triggerer
  const handleTestWebhook = () => {
    if (!integrationSettings.webhookUrl) {
      showToast(
        language === 'en' ? 'Please supply a Webhook URL first.' : 
        (language === 'es' ? 'Por favor ingrese una URL' : 'Por favor, insira uma URL de Webhook válida primeiro.'), 
        'error'
      );
      return;
    }
    
    setIsTestingWebhook(true);
    setWebhookTestResult(null);
    
    setTimeout(() => {
      setWebhookTestResult({
        timestamp: new Date().toISOString(),
        status: 200,
        statusText: 'OK',
        payloadSent: {
          event: 'lead.created',
          lead: {
            id: 'mock-lead-7819',
            name: 'Joseval Teste Webhook',
            email: 'geradordeoportunidades@gmail.com',
            phone: '11999999999'
          },
          platform: 'OptiVelo Live v2.0'
        },
        response: {
          success: true,
          message: 'Webhook payload received successfully by destination.'
        }
      });
      setIsTestingWebhook(false);
      showToast('Webhook de teste enviado com sucesso! Status 200 OK.', 'success');
    }, 2000);
  };

  // Safe Card Update
  const handleUpdateCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;
    
    if (!cardForm.number || !cardForm.expiry || !cardForm.cvv) {
      showToast('Por favor preencha todos os campos do cartão.', 'error');
      return;
    }

    setSaving(true);
    try {
      const brand = cardForm.number.startsWith('4') ? 'Visa' : cardForm.number.startsWith('3') ? 'Amex' : 'MasterCard';
      const last4 = cardForm.number.slice(-4) || '4242';
      const updatedCard = {
        brand,
        last4,
        expiry: cardForm.expiry,
        holderName: cardForm.name || 'Titular da Conta'
      };
      
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        cardDetails: updatedCard
      });
      
      setCardDetails(updatedCard);
      setIsEditingCard(false);
      setCardForm({ number: '', expiry: '', cvv: '', name: '' });
      showToast('Método de pagamento atualizado com segurança!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Falha ao atualizar cartão no cofre seguro.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Clean 2FA Mock verifier
  const handleVerifyMfa = () => {
    if (mfaCode.length !== 6) {
      showToast('O código verificador deve ter 6 dígitos.', 'error');
      return;
    }
    
    setIsMfaVerifying(true);
    setTimeout(() => {
      setIsMfaVerifying(false);
      setMfaConfigured(true);
      showToast('Autenticação de Duas Etapas configurada com absoluto sucesso!', 'success');
      setMfaCode('');
    }, 1500);
  };

  if (loading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-12 max-w-6xl mx-auto space-y-8 animate-fade-in pb-32">
      <div className="space-y-1">
        <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <Settings className="w-6 h-6 lg:w-8 lg:h-8 text-slate-400" />
          Configurações
        </h1>
        <p className="text-xs lg:text-sm text-slate-500">
          Gerencie sua conta, canais de notificações, integrações de pixels e segurança de tráfego.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Left Navigation Submenu Block */}
        <div className="lg:col-span-1 space-y-4">
          <div className="premium-card p-4 space-y-2">
            <h3 className="px-3 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Ajustes & Sistema</h3>
            <nav className="space-y-1">
              <SettingsNavItem 
                icon={User} 
                label={getTabLabel('profile')} 
                active={activeTab === 'profile'} 
                onClick={() => setActiveTab('profile')}
              />
              <SettingsNavItem 
                icon={Globe} 
                label={getTabLabel('connector')} 
                active={activeTab === 'connector'} 
                onClick={() => setActiveTab('connector')}
                locked={userData?.role !== 'admin' && !['scale', 'enterprise'].includes(userData?.subscriptionStatus)}
              />
              <SettingsNavItem 
                icon={CreditCard} 
                label={getTabLabel('subscription')} 
                active={activeTab === 'subscription'}
                onClick={() => setActiveTab('subscription')}
              />
              <SettingsNavItem 
                icon={Shield} 
                label={getTabLabel('security')} 
                active={activeTab === 'security'}
                onClick={() => setActiveTab('security')}
              />
              <SettingsNavItem 
                icon={Bell} 
                label={getTabLabel('notifications')} 
                active={activeTab === 'notifications'}
                onClick={() => setActiveTab('notifications')}
              />
              <SettingsNavItem 
                icon={Globe} 
                label={getTabLabel('integrations')} 
                active={activeTab === 'integrations'}
                onClick={() => setActiveTab('integrations')}
              />
            </nav>
          </div>
        </div>

        {/* Right Tab Content Viewer */}
        <div className="lg:col-span-3 space-y-6">
          <form onSubmit={handleSave} className="premium-card p-6 lg:p-8 space-y-8">
            
            {/* TAB: PROFILE */}
            {activeTab === 'profile' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4">
                  <h3 className="text-lg font-bold text-white">Informações do Perfil</h3>
                  <p className="text-xs text-slate-500">Gerencie suas credenciais de contato e preferências regionais.</p>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-700 flex items-center justify-center text-xl font-bold text-white border border-white/10 shadow-lg">
                      {displayName?.charAt(0) || auth.currentUser?.email?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{displayName || 'Membro Fundador'}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">{userData?.role || 'user'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="micro-label">Nome de Exibição</label>
                      <input 
                        type="text" 
                        required
                        className="premium-input w-full"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Seu nome completo"
                      />
                    </div>
                    <div className="space-y-2 bg-white/[0.01] border border-white/5 rounded-2xl p-4">
                      <label className="micro-label">E-mail de Cadastro (Não Editável)</label>
                      <p className="text-sm text-slate-300 font-medium select-all pt-1 break-all">
                        {userData?.email || auth.currentUser?.email}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="micro-label">Idioma da Plataforma</label>
                    <select 
                      value={prefLanguage}
                      onChange={(e) => setPrefLanguage(e.target.value as any)}
                      className="premium-input bg-black/60 text-white w-full py-3"
                    >
                      <option value="pt" className="bg-neutral-900 text-white">Português (BR)</option>
                      <option value="en" className="bg-neutral-900 text-white">English (US)</option>
                      <option value="es" className="bg-neutral-900 text-white">Español (ES)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: CONNECTOR QUANTUM */}
            {activeTab === 'connector' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">Conector Quantum</h3>
                    <p className="text-xs text-slate-500">Gerencie a otimização de IA no seu site ou landing page externa.</p>
                  </div>
                  <div className="flex items-center gap-2 px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded text-[10px] font-bold text-emerald-400 uppercase tracking-widest">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Ativo e Monitorando
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl space-y-4">
                    <div className="flex items-center gap-3 text-indigo-400">
                      <Globe className="w-5 h-5 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-widest">Script Amigável</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Copie o script de alta velocidade abaixo e injete dentro da tag <code className="text-indigo-300 bg-white/5 px-1.5 py-0.5 rounded font-mono">&lt;head&gt;</code> de seu site WordPress, Wix ou similar para rastreamento instantâneo.
                    </p>
                    
                    <div className="relative group">
                      <pre className="p-4 bg-black/40 border border-white/10 rounded-xl text-[10px] text-indigo-300 font-mono overflow-x-auto pr-12 select-all leading-normal">
                        {`<script src="https://cdn.optivelo.com.br/quantum-pixel.js?id=${auth.currentUser?.uid}"></script>`}
                      </pre>
                      <button 
                        type="button"
                        onClick={() => {
                          const scriptText = `<script src="https://cdn.optivelo.com.br/quantum-pixel.js?id=${auth.currentUser?.uid}"></script>`;
                          try {
                            if (navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(scriptText);
                              showToast('Código inteligente copiado para a área de transferência!', 'success');
                            } else {
                              const textArea = document.createElement("textarea");
                              textArea.value = scriptText;
                              textArea.style.position = "fixed";
                              textArea.style.opacity = "0";
                              document.body.appendChild(textArea);
                              textArea.select();
                              const successful = document.execCommand('copy');
                              document.body.removeChild(textArea);
                              if (successful) {
                                showToast('Código inteligente copiado para a área de transferência!', 'success');
                              } else {
                                throw new Error('execCommand failed');
                              }
                            }
                          } catch (err) {
                            console.warn('Falha na API Clipboard do Iframe, usando prompt:', err);
                            window.prompt('Copie o código abaixo manualmente:', scriptText);
                          }
                        }}
                        className="absolute top-2 right-2 p-2 bg-white/5 hover:bg-white/10 hover:text-white rounded-lg transition-colors text-slate-400"
                        title="Copiar Código"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Otimizações Externas</h4>
                    
                    <div className="space-y-3">
                      <ConnectorToggle 
                        label="IA de Substituição Neural" 
                        desc="Muda títulos, imagens de botões ou copys automaticamente conforme o comportamento do cliente externo."
                        active={connectorSettings.neuralReplacement}
                        onClick={() => setConnectorSettings(s => ({ ...s, neuralReplacement: !s.neuralReplacement }))}
                      />
                      <ConnectorToggle 
                        label="Captura Inteligente e Popups" 
                        desc="Mecanismo nativo de saída (exit intent) para colher e-mails de visitantes que tentam abandonar a página."
                        active={connectorSettings.smartCapture}
                        onClick={() => setConnectorSettings(s => ({ ...s, smartCapture: !s.smartCapture }))}
                      />
                      <ConnectorToggle 
                        label="Fluxos de Behavior Trackers" 
                        desc="Mapeia cliques e gera estatísticas automáticas de zonas de calor para melhorar a conversão."
                        active={connectorSettings.behaviorTracking}
                        onClick={() => setConnectorSettings(s => ({ ...s, behaviorTracking: !s.behaviorTracking }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: SUBSCRIPTION */}
            {activeTab === 'subscription' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">Gestão de Assinatura</h3>
                    <p className="text-xs text-slate-500">Acompanhe seus limites de tráfego, consumo de IA e faturamentos.</p>
                  </div>
                  <div className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded-full text-xs font-bold uppercase">
                    {userData?.subscriptionStatus || 'Trial'}
                  </div>
                </div>

                {/* Quota meters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/[0.01] border border-white/5 p-4 rounded-2xl space-y-3">
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Visualizações Sincronizadas</span>
                      <span className="font-bold text-white">4.2%</span>
                    </div>
                    <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: '4.2%' }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>843 usandos</span>
                      <span>20.000 visualizações/mês</span>
                    </div>
                  </div>

                  <div className="bg-white/[0.01] border border-white/5 p-4 rounded-2xl space-y-3">
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Páginas de IA Ativas</span>
                      <span className="font-bold text-white">40.0%</span>
                    </div>
                    <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: '40%' }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>2 criadas</span>
                      <span>Limite: 5 páginas</span>
                    </div>
                  </div>

                  <div className="bg-white/[0.01] border border-white/5 p-4 rounded-2xl space-y-3">
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Créditos de Escrita Neural</span>
                      <span className="font-bold text-white">13.3%</span>
                    </div>
                    <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: '13.3%' }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500">
                      <span>4 / 30 gerações</span>
                      <span>Reinicia em 7 dias</span>
                    </div>
                  </div>
                </div>

                {/* Card Payment Info */}
                <div className="bg-white/[0.01] border border-white/5 p-6 rounded-2xl space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-indigo-400" />
                      <div>
                        <p className="text-xs font-bold text-white">Método de faturamento seguro</p>
                        <p className="text-[11px] text-slate-400">
                          {cardDetails.brand} finalizado em <strong>{cardDetails.last4}</strong> • Exp {cardDetails.expiry}
                        </p>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setIsEditingCard(!isEditingCard)}
                      className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 text-[10px] font-bold uppercase transition-all tracking-wider text-slate-300"
                    >
                      {isEditingCard ? 'Cancelar' : 'Alterar Cartão'}
                    </button>
                  </div>

                  {isEditingCard && (
                    <div className="p-4 bg-black/40 border border-white/10 rounded-xl space-y-4 animate-slide-down">
                      <p className="text-[10px] uppercase font-bold text-indigo-400">Novo Cartão de Crédito</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-slate-500 uppercase">Número do Cartão</label>
                          <input 
                            type="text" 
                            className="premium-input text-xs" 
                            placeholder="4111 2222 3333 4444" 
                            maxLength={16}
                            value={cardForm.number}
                            onChange={e => setCardForm({...cardForm, number: e.target.value.replace(/\D/g, '')})}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono text-slate-500 uppercase">Nome do Titular</label>
                          <input 
                            type="text" 
                            className="premium-input text-xs" 
                            placeholder="Ex: JOSEVAL PEREIRA" 
                            value={cardForm.name}
                            onChange={e => setCardForm({...cardForm, name: e.target.value.toUpperCase()})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-slate-500 uppercase">Validade</label>
                            <input 
                              type="text" 
                              className="premium-input text-xs" 
                              placeholder="MM/AA" 
                              maxLength={5}
                              value={cardForm.expiry}
                              onChange={e => setCardForm({...cardForm, expiry: e.target.value})}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono text-slate-500 uppercase">CVV</label>
                            <input 
                              type="password" 
                              className="premium-input text-xs" 
                              placeholder="123" 
                              maxLength={4}
                              value={cardForm.cvv}
                              onChange={e => setCardForm({...cardForm, cvv: e.target.value.replace(/\D/g, '')})}
                            />
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={handleUpdateCard}
                        className="w-full py-2 bg-indigo-500 text-white font-bold text-[10px] uppercase tracking-widest rounded-lg hover:bg-indigo-400 transition-all flex items-center justify-center gap-1.5"
                      >
                        <Save className="w-3.5 h-3.5" /> Atualizar dados de Pagamento
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing invoice summary */}
                <div className="space-y-3">
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Histórico de Cobrança</h4>
                  <div className="bg-white/[0.01] border border-white/5 rounded-2xl overflow-hidden">
                    <div className="p-4 bg-white/[0.02] border-b border-white/5 grid grid-cols-4 text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                      <span>Data</span>
                      <span className="col-span-2">Descrição</span>
                      <span className="text-right">Valor</span>
                    </div>
                    <div className="divide-y divide-white/5">
                      <div className="p-4 grid grid-cols-4 items-center text-xs text-slate-300">
                        <span className="font-mono text-[11px]">Launched</span>
                        <span className="col-span-2 font-semibold">Fundadores Promo OptiVelo • Mensal</span>
                        <span className="text-right font-bold text-emerald-400 font-mono">Gratuito / Trial</span>
                      </div>
                      <div className="p-4 grid grid-cols-4 items-center text-xs text-slate-300">
                        <span className="font-mono text-[11px]">08/06/2026</span>
                        <span className="col-span-2 font-semibold">Cupom Promocional OptiVelo Pro (Trial Ativo)</span>
                        <span className="text-right font-bold text-emerald-400 font-mono">R$ 0,00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: SECURITY */}
            {activeTab === 'security' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4">
                  <h3 className="text-lg font-bold text-white">Segurança da Conta</h3>
                  <p className="text-xs text-slate-500">Mantenha sua conta OptiVelo monitorada com os melhores padrões de criptografia.</p>
                </div>

                <div className="space-y-6">
                  {/* Change Password Input Blocks */}
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                      <Key className="w-3.5 h-3.5" /> Redefinir Senha
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="micro-label">Nova Senha</label>
                        <input 
                          type="password" 
                          className="premium-input w-full"
                          value={passwords.newPass}
                          onChange={e => setPasswords({ ...passwords, newPass: e.target.value })}
                          placeholder="Mínimo 6 caracteres"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="micro-label">Confirme a Nova Senha</label>
                        <input 
                          type="password" 
                          className="premium-input w-full"
                          value={passwords.confirm}
                          onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
                          placeholder="Repita a senha escrita"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 2FA Section */}
                  <div className="bg-white/[0.01] border border-white/5 p-6 rounded-2xl space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <p className="text-xs font-bold text-white flex items-center gap-1.5">
                          <Smartphone className="w-4 h-4 text-emerald-400" />
                          Autenticação em Duas Etapas (MFA)
                        </p>
                        <p className="text-[10px] text-slate-500">
                          Configure um código 2FA usando o Google Authenticator ou Microsoft Authenticator.
                        </p>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          if (mfaConfigured) {
                            setMfaConfigured(false);
                            setTwoFactorEnabled(false);
                            showToast('Duas Etapas desativado com sucesso.', 'info');
                          } else {
                            setTwoFactorEnabled(true);
                          }
                        }}
                        className={`w-10 h-5 rounded-full transition-colors relative ${mfaConfigured ? 'bg-indigo-500' : 'bg-slate-800'}`}
                      >
                        <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${mfaConfigured ? 'left-6' : 'left-1'}`} />
                      </button>
                    </div>

                    {twoFactorEnabled && !mfaConfigured && (
                      <div className="p-4 bg-black/40 border border-white/10 rounded-xl space-y-4 animate-slide-down">
                        <div className="flex flex-col md:flex-row items-center gap-6">
                          {/* Dotted stylish QR mockup */}
                          <div className="w-32 h-32 bg-white p-2 rounded-xl border border-indigo-500/30 flex flex-col items-center justify-center relative group">
                            <div className="w-full h-full bg-slate-930 rounded border border-dashed border-slate-700 flex flex-around items-center justify-center text-[8px] font-mono text-slate-400 p-2">
                              {/* QR Design */}
                              <div className="grid grid-cols-5 gap-1.5">
                                <div className="w-4 h-4 bg-indigo-950 border border-indigo-500"></div>
                                <div className="w-4 h-4 bg-indigo-550"></div>
                                <div className="w-4 h-4 bg-transparent"></div>
                                <div className="w-4 h-4 bg-indigo-500"></div>
                                <div className="w-4 h-4 bg-indigo-950 border border-indigo-500"></div>
                                
                                <div className="w-4 h-4 bg-transparent"></div>
                                <div className="w-4 h-4 bg-indigo-950"></div>
                                <div className="w-4 h-4 bg-indigo-500"></div>
                                <div className="w-4 h-4 bg-indigo-950"></div>
                                <div className="w-4 h-4 bg-transparent"></div>

                                <div className="w-4 h-4 bg-indigo-500"></div>
                                <div className="w-4 h-4 bg-transparent"></div>
                                <div className="w-4 h-4 bg-indigo-950 border border-indigo-400 animate-pulse"></div>
                                <div className="w-4 h-4 bg-indigo-500"></div>
                                <div className="w-4 h-4 bg-indigo-950"></div>

                                <div className="w-4 h-4 bg-indigo-950 border border-indigo-500"></div>
                                <div className="w-4 h-4 bg-indigo-500"></div>
                                <div className="w-4 h-4 bg-transparent"></div>
                                <div className="w-4 h-4 bg-indigo-950"></div>
                                <div className="w-4 h-4 bg-indigo-950 border border-indigo-500"></div>
                              </div>
                            </div>
                            <span className="absolute bottom-1 right-1 text-[7px] text-slate-400 italic">OptiVelo SEC</span>
                          </div>

                          <div className="flex-1 space-y-3">
                            <p className="text-[11px] text-slate-300 leading-relaxed">
                              Escaneie o código acima no app autenticador ou digite a chave:
                              <br />
                              <span className="font-mono text-indigo-400 font-bold tracking-tight select-all">
                                OPTIVELO-2FA-SEC-KEY-2026
                              </span>
                            </p>
                            <div className="flex gap-2">
                              <input 
                                type="text"
                                className="premium-input text-center text-sm font-bold tracking-widest max-w-[120px]"
                                placeholder="000 000"
                                maxLength={6}
                                value={mfaCode}
                                onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                              />
                              <button
                                type="button"
                                onClick={handleVerifyMfa}
                                disabled={isMfaVerifying}
                                className="px-4 bg-white text-black font-bold uppercase tracking-widest text-[10px] rounded-lg hover:bg-slate-200 transition-all flex items-center gap-1"
                              >
                                {isMfaVerifying && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Validar Token 
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sessions logs */}
                  <div className="space-y-3 bg-white/[0.01] border border-white/5 p-6 rounded-2xl">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs font-bold text-white uppercase tracking-tight">Registro de Dispositivos Conectados</p>
                        <p className="text-[10px] text-slate-500">Estes são os navegadores que acessaram sua conta atualmente.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRevokeSessions}
                        disabled={revokingSessions || activeSessions.length <= 1}
                        className="px-3 py-1 bg-rose-500/10 border border-rose-500/20 text-rose-500 hover:bg-rose-500/20 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1 disabled:opacity-50"
                      >
                        {revokingSessions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
                        Sair dos demais aparelhos
                      </button>
                    </div>

                    <div className="space-y-2 mt-4">
                      {activeSessions.map(sess => (
                        <div key={sess.id} className="flex justify-between items-center p-3 bg-black/40 border border-white/5 rounded-xl text-xs">
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${sess.active ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                            <div>
                              <p className="font-semibold text-white">{sess.device}</p>
                              <p className="text-[10px] text-slate-500 font-mono">{sess.ip} • {sess.location}</p>
                            </div>
                          </div>
                          <span className="text-[10px] bg-white/5 px-2 py-0.5 rounded text-slate-400 font-mono">
                            {sess.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB: NOTIFICATIONS */}
            {activeTab === 'notifications' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4">
                  <h3 className="text-lg font-bold text-white">Preferências de Notificação</h3>
                  <p className="text-xs text-slate-500">Regule os e-mails e inteligências sobre novos leads de vendas.</p>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Opções de Alerta de Tráfego</h4>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <ConnectorToggle 
                      label="Alertas Instantâneos de Conversão" 
                      desc="Dispara um e-mail de alerta completo no seu e-mail a cada lead novo pego pelas landing pages."
                      active={notificationSettings?.emailConversions || false}
                      onClick={() => setNotificationSettings(s => ({ ...s, emailConversions: !s?.emailConversions }))}
                    />
                    <ConnectorToggle 
                      label="Relatório de Otimização Semanal" 
                      desc="Diagnósticos semanais de tráfego, cliques e recomendações de cópias alternativas da IA Neural."
                      active={notificationSettings?.weeklyReport || false}
                      onClick={() => setNotificationSettings(s => ({ ...s, weeklyReport: !s?.weeklyReport }))}
                    />
                    <ConnectorToggle 
                      label="Avisos e Segurança do Servidor" 
                      desc="Notificações imediatas sobre falha na publicação, domínio de teste ou limites do script."
                      active={notificationSettings?.securityAlerts || false}
                      onClick={() => setNotificationSettings(s => ({ ...s, securityAlerts: !s?.securityAlerts }))}
                    />
                    <ConnectorToggle 
                      label="Campanhas, Novidades & Hacks de Venda" 
                      desc="Promoções de ecossistema, eventos Quantum e tutoriais avançados de escala de negócios."
                      active={notificationSettings?.marketingUpdates || false}
                      onClick={() => setNotificationSettings(s => ({ ...s, marketingUpdates: !s?.marketingUpdates }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* TAB: INTEGRATIONS */}
            {activeTab === 'integrations' && (
              <div className="space-y-6 animate-fade-in">
                <div className="border-b border-white/5 pb-4">
                  <h3 className="text-lg font-bold text-white">Gerenciador de Integrações e Webhooks</h3>
                  <p className="text-xs text-slate-500">Conecte o tráfego do OptiVelo aos seus painéis de rastreamento de anúncios favoritos.</p>
                </div>

                <div className="space-y-6">
                  {/* Google and Facebook Analytics details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 premium-card p-6 border border-white/5">
                      <div className="flex items-center gap-2 text-indigo-400 pb-2">
                        <Globe className="w-5 h-5" />
                        <h4 className="text-xs font-bold uppercase tracking-widest">Google Analytics 4</h4>
                      </div>
                      <label className="text-[9px] font-mono text-slate-500 uppercase">ID de Medição (Measurement ID)</label>
                      <input 
                        type="text" 
                        className="premium-input w-full"
                        value={integrationSettings.gaId}
                        onChange={e => setIntegrationSettings({
                          ...integrationSettings,
                          gaId: e.target.value
                        })}
                        placeholder="Ex: G-XXXXXXXXXX"
                      />
                      <p className="text-[9px] text-slate-500 leading-normal pt-1">
                        Injeta tags gtag do Google no pixel inteligente Quantum sincronizando todas as métricas automaticamente.
                      </p>
                    </div>

                    <div className="space-y-2 premium-card p-6 border border-white/5">
                      <div className="flex items-center gap-2 text-indigo-400 pb-2">
                        <Cpu className="w-5 h-5 animate-pulse" />
                        <h4 className="text-xs font-bold uppercase tracking-widest">Meta Facebook Pixel</h4>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <label className="text-[9px] font-mono text-slate-500 uppercase">ID do Pixel (Pixel ID)</label>
                          <input 
                            type="text" 
                            className="premium-input w-full text-xs"
                            value={integrationSettings.pixelId}
                            onChange={e => setIntegrationSettings({
                              ...integrationSettings,
                              pixelId: e.target.value.replace(/\D/g, '')
                            })}
                            placeholder="Ex: 89104829302193"
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-500 uppercase">API de Conversões Token (opcional)</label>
                          <input 
                            type="password" 
                            className="premium-input w-full text-xs"
                            value={integrationSettings.pixelToken}
                            onChange={e => setIntegrationSettings({
                              ...integrationSettings,
                              pixelToken: e.target.value
                            })}
                            placeholder="Ex: EAAFZB19z3O4BA..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Webhooks integrations wrapper */}
                  <div className="premium-card p-6 border border-indigo-500/10 bg-indigo-500/[0.01] rounded-2xl space-y-4">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold text-white uppercase tracking-widest flex items-center gap-2">
                          <Send className="w-4 h-4 text-indigo-400" />
                          Integração via Webhook
                        </h4>
                        <p className="text-[10px] text-slate-400 leading-normal">
                          Transmita dados de todos os leads de suas páginas e campanhas em tempo real em formato JSON.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-[9px] font-mono text-slate-500 uppercase">Endereço de Destino (Endpoint URL)</label>
                        <div className="flex gap-2">
                          <input 
                            type="url" 
                            className="premium-input flex-1 text-xs font-mono text-indigo-300"
                            value={integrationSettings.webhookUrl}
                            onChange={e => setIntegrationSettings({
                              ...integrationSettings,
                              webhookUrl: e.target.value
                            })}
                            placeholder="https://sua-api-crm.com/v1/webhook"
                          />
                          <button
                            type="button"
                            onClick={handleTestWebhook}
                            disabled={isTestingWebhook || !integrationSettings.webhookUrl}
                            className="px-4 bg-indigo-500 text-white font-bold text-[10px] uppercase tracking-widest rounded-xl hover:bg-indigo-400 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                          >
                            {isTestingWebhook ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Testar
                          </button>
                        </div>
                      </div>

                      {/* Event checklists */}
                      <div className="space-y-2 pt-2">
                        <p className="text-[9px] font-mono text-slate-500 uppercase">Gatilhos Ativos para disparo do Webhook</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <label className="flex items-center gap-2 p-3 bg-black/40 border border-white/5 rounded-xl cursor-pointer select-none">
                            <input 
                              type="checkbox" 
                              checked={integrationSettings?.webhookEvents?.leadCaptured || false} 
                              onChange={e => setIntegrationSettings({
                                ...integrationSettings,
                                webhookEvents: {
                                  leadCaptured: e.target.checked,
                                  planUpdated: integrationSettings?.webhookEvents?.planUpdated ?? false,
                                  domainConfigured: integrationSettings?.webhookEvents?.domainConfigured ?? false
                                }
                              })}
                              className="accent-indigo-500 rounded text-indigo-500"
                            />
                            <div className="text-[10px] text-slate-400 leading-none">
                              <p className="font-bold text-white">Lead Capturada</p>
                            </div>
                          </label>

                          <label className="flex items-center gap-2 p-3 bg-black/40 border border-white/5 rounded-xl cursor-pointer select-none">
                            <input 
                              type="checkbox" 
                              checked={integrationSettings?.webhookEvents?.planUpdated || false} 
                              onChange={e => setIntegrationSettings({
                                ...integrationSettings,
                                webhookEvents: {
                                  leadCaptured: integrationSettings?.webhookEvents?.leadCaptured ?? true,
                                  planUpdated: e.target.checked,
                                  domainConfigured: integrationSettings?.webhookEvents?.domainConfigured ?? false
                                }
                              })}
                              className="accent-indigo-500 rounded text-indigo-500"
                            />
                            <div className="text-[10px] text-slate-400 leading-none">
                              <p className="font-bold text-white">Assinatura Atualizada</p>
                            </div>
                          </label>

                          <label className="flex items-center gap-2 p-3 bg-black/40 border border-white/5 rounded-xl cursor-pointer select-none">
                            <input 
                              type="checkbox" 
                              checked={integrationSettings?.webhookEvents?.domainConfigured || false} 
                              onChange={e => setIntegrationSettings({
                                ...integrationSettings,
                                webhookEvents: {
                                  leadCaptured: integrationSettings?.webhookEvents?.leadCaptured ?? true,
                                  planUpdated: integrationSettings?.webhookEvents?.planUpdated ?? false,
                                  domainConfigured: e.target.checked
                                }
                              })}
                              className="accent-indigo-500 rounded text-indigo-500"
                            />
                            <div className="text-[10px] text-slate-400 leading-none">
                              <p className="font-bold text-white">Domínio Customizado</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Webhook tester console mockup */}
                      {webhookTestResult && (
                        <div className="p-4 bg-zinc-950/60 border border-zinc-900 rounded-xl space-y-3 font-mono text-[10px] animate-slide-down">
                          <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
                            <span className="text-zinc-500">CONSOLA DE TESTE WEBHOOK</span>
                            <span className="text-emerald-400 flex items-center gap-1">
                              <CheckCircle className="w-3.5 h-3.5" />
                              HTTP {webhookTestResult.status} {webhookTestResult.statusText}
                            </span>
                          </div>
                          <div className="space-y-1 text-slate-400">
                            <p className="text-zinc-500">• Payload enviado:</p>
                            <pre className="p-3 bg-black/40 border border-white/5 text-indigo-300 rounded overflow-x-auto select-all">
                              {JSON.stringify(webhookTestResult.payloadSent, null, 2)}
                            </pre>
                            <p className="text-zinc-500 pt-2">• Resposta do Servidor {integrationSettings.webhookUrl}:</p>
                            <pre className="p-3 bg-black/40 border border-white/5 text-slate-300 rounded overflow-x-auto">
                              {JSON.stringify(webhookTestResult.response, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Action Button */}
            {activeTab !== 'subscription' && (
              <button 
                type="submit"
                disabled={saving}
                className="w-full py-4 bg-white text-black font-bold rounded-2xl hover:bg-slate-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50 uppercase tracking-widest text-[10px]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar Alterações
              </button>
            )}
          </form>

          {/* Bottom Card redirects if active */}
          <div className="premium-card p-6 lg:p-8 space-y-6">
            <h3 className="text-lg font-bold text-white">Plano Atual</h3>
            <div className="p-6 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="text-xl font-bold text-white uppercase tracking-tighter">
                  {userData?.subscriptionStatus || 'Trial'}
                </p>
                <p className="text-xs text-slate-500">
                  {userData?.subscriptionStatus === 'trial' ? 'Período de teste com escopo Quantum ativo' : 'Assinatura segura e ativa com recursos estendidos'}
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setActiveTab('subscription')}
                className="px-4 py-3 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-indigo-400 transition-all flex items-center justify-center gap-1.5"
              >
                <CreditCard className="w-3.5 h-3.5" /> Gerenciar Plano
              </button>
            </div>
          </div>

          <div className="premium-card p-6 lg:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Sistema</h3>
              <span className="text-[11px] font-bold text-emerald-500 uppercase tracking-widest">v2.0.1 Quantum</span>
            </div>
            <div className="p-6 bg-amber-500/5 border border-amber-500/10 rounded-2xl space-y-4">
              <div className="space-y-1">
                <p className="text-sm font-bold text-amber-500 uppercase tracking-tight">Cache do Navegador</p>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Se você não estiver vendo as atualizações mais recentes (como o novo ecossistema Quantum), 
                  pode ser que seu navegador esteja guardando uma versão antiga.
                </p>
              </div>
              <button 
                type="button"
                onClick={() => window.location.reload()}
                className="w-full py-3 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-amber-500/20 transition-all flex items-center justify-center gap-2"
              >
                <Zap className="w-3 h-3" /> Forçar Atualização do Sistema
              </button>
            </div>
          </div>

          <button 
            type="button"
            onClick={() => auth.signOut()}
            className="w-full py-4 bg-rose-500/10 border border-rose-500/20 text-rose-500 font-bold rounded-2xl hover:bg-rose-500/20 transition-all flex items-center justify-center gap-2 uppercase tracking-widest text-[10px]"
          >
            <LogOut className="w-4 h-4" />
            Sair da Conta
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsNavItem = ({ icon: Icon, label, active, onClick, locked }: { icon: any, label: string, active?: boolean, onClick?: () => void, locked?: boolean }) => (
  <button 
    type="button"
    onClick={locked ? undefined : onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
    active ? 'bg-white/5 text-white' : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.02]'
  } ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <Icon className={`w-4 h-4 ${active ? 'text-indigo-400' : ''}`} />
    <span className="flex-1 text-left">{label}</span>
    {locked && <Lock className="w-3 h-3 text-slate-600" />}
  </button>
);

const ConnectorToggle = ({ label, desc, active, onClick }: { label: string, desc: string, active: boolean, onClick: () => void }) => (
  <div className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/5 rounded-2xl hover:bg-white/[0.04] transition-all">
    <div className="space-y-0.5 flex-1 pr-4">
      <p className="text-xs font-bold text-white">{label}</p>
      <p className="text-[10px] text-slate-500 leading-normal">{desc}</p>
    </div>
    <button 
      type="button"
      onClick={onClick}
      className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${active ? 'bg-indigo-500' : 'bg-slate-800'}`}
    >
      <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${active ? 'left-6' : 'left-1'}`} />
    </button>
  </div>
);
